import { ContainerBrowser } from '../container-browser.js';
import { showStatusNotification } from '../utils.js';

export interface ContainerPageConfig {
  containerId: string;
  containerName: string;
  containerColor: string;
}

export class ContainerPage {
  private container: HTMLElement;
  private containerBrowser: ContainerBrowser | null = null;
  private config: ContainerPageConfig;
  private mountTimeoutId: number | null = null;
  private loadedNotificationTimeoutId: number | null = null;
  private isDestroyed = false;

  constructor(config: ContainerPageConfig) {
    this.config = config;
    this.container = this.createContainer();
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'container-page';
    container.style.cssText = `
      min-height: 100vh;
      background: var(--background-secondary);
    `;

    // Create header with navigation
    const header = this.createHeader();
    container.appendChild(header);

    // Create main content area
    const mainContent = this.createMainContent();
    container.appendChild(mainContent);

    return container;
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('header');
    header.className = 'container-header';
    header.style.cssText = `
      background: var(--background-primary);
      box-shadow: var(--shadow-sm);
      position: sticky;
      top: 0;
      z-index: 100;
      border-bottom: 1px solid var(--border-color);
    `;

    const headerContent = document.createElement('div');
    headerContent.style.cssText = `
      max-width: 1200px;
      margin: 0 auto;
      padding: var(--spacing-md) var(--spacing-xl);
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;

    // Left side: Logo, back button, and title
    const leftSection = document.createElement('div');
    leftSection.style.cssText = `
      display: flex;
      align-items: center;
      gap: var(--spacing-md);
    `;

    // Add USM logo
    const logo = document.createElement('img');
    logo.src = '/usm-logo.webp';
    logo.alt = 'USM Logo';
    logo.style.cssText = `
      height: 40px;
      width: 40px;
      object-fit: contain;
    `;

    const backButton = document.createElement('button');
    backButton.innerHTML = '← Back';
    backButton.style.cssText = `
      background: white;
      color: var(--text-primary);
      border: 1px solid var(--text-primary);
      padding: var(--spacing-md) var(--spacing-lg);
      border-radius: var(--border-radius-md);
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      min-height: 44px;
      min-width: 44px;
    `;

    backButton.addEventListener('mouseenter', () => {
      backButton.style.background = 'var(--text-primary)';
      backButton.style.color = 'white';
    });

    backButton.addEventListener('mouseleave', () => {
      backButton.style.background = 'white';
      backButton.style.color = 'var(--text-primary)';
    });

    backButton.addEventListener('click', () => {
      window.location.href = '/';
    });

    const title = document.createElement('h1');
    title.textContent = this.config.containerName;
    title.style.cssText = `
      font-size: 1.8rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0;
    `;

    leftSection.appendChild(logo);
    leftSection.appendChild(backButton);
    leftSection.appendChild(title);

    // Right side: Connection status
    const rightSection = document.createElement('div');
    rightSection.style.cssText = `
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      font-size: 0.875rem;
    `;

    const statusIndicator = document.createElement('span');
    statusIndicator.id = 'statusIndicator';
    statusIndicator.className = 'status-indicator connecting';
    statusIndicator.style.cssText = `
      width: 8px;
      height: 8px;
      border-radius: 50%;
    `;

    const statusText = document.createElement('span');
    statusText.id = 'statusText';
    statusText.textContent = 'Connecting...';
    statusText.style.cssText = `
      color: var(--text-secondary);
    `;

    rightSection.appendChild(statusIndicator);
    rightSection.appendChild(statusText);

    headerContent.appendChild(leftSection);
    headerContent.appendChild(rightSection);
    header.appendChild(headerContent);

    return header;
  }

  private createMainContent(): HTMLElement {
    const mainContent = document.createElement('main');
    mainContent.className = 'container-main';
    mainContent.style.cssText = `
      max-width: 1200px;
      margin: 0 auto;
      padding: var(--spacing-xl);
    `;

    // Create search and filters container
    const searchContainer = this.createSearchContainer();
    mainContent.appendChild(searchContainer);

    // Create PDF grid container
    const pdfContainer = this.createPDFContainer();
    mainContent.appendChild(pdfContainer);

    return mainContent;
  }

  private createSearchContainer(): HTMLElement {
    const searchContainer = document.createElement('div');
    searchContainer.className = 'search-container';
    searchContainer.style.cssText = `
      margin-bottom: var(--spacing-xl);
    `;

    const searchBox = document.createElement('div');
    searchBox.style.cssText = `
      display: flex;
      gap: var(--spacing-md);
      align-items: center;
      flex-wrap: wrap;
    `;

    // Search input
    const searchInput = document.createElement('input');
    searchInput.id = 'searchInput';
    searchInput.type = 'text';
    searchInput.placeholder = 'Search PDFs...';
    searchInput.style.cssText = `
      flex: 1;
      min-width: 250px;
      padding: var(--spacing-md);
      border: 2px solid var(--border-color);
      border-radius: var(--border-radius-md);
      font-size: 1rem;
      min-height: 44px;
      background: var(--background-primary);
      color: var(--text-primary);
    `;

    // Clear search button
    const clearSearchBtn = document.createElement('button');
    clearSearchBtn.id = 'clearSearch';
    clearSearchBtn.innerHTML = '×';
    clearSearchBtn.title = 'Clear search';
    clearSearchBtn.style.cssText = `
      background: var(--background-tertiary);
      border: 2px solid var(--border-color);
      border-radius: var(--border-radius-md);
      padding: var(--spacing-md);
      font-size: 1.2rem;
      cursor: pointer;
      min-height: 44px;
      min-width: 44px;
      transition: var(--transition-fast);
      color: var(--text-primary);
    `;

    // Sort select
    const sortSelect = document.createElement('select');
    sortSelect.id = 'sortSelect';
    sortSelect.style.cssText = `
      padding: var(--spacing-md);
      border: 2px solid var(--border-color);
      border-radius: var(--border-radius-md);
      font-size: 1rem;
      background: var(--background-primary);
      min-height: 44px;
      min-width: 150px;
      color: var(--text-primary);
    `;

    const sortOptions = [
      { value: 'date-desc', text: 'Newest First' },
      { value: 'date-asc', text: 'Oldest First' },
      { value: 'name-asc', text: 'Name A-Z' },
      { value: 'name-desc', text: 'Name Z-A' },
      { value: 'size-desc', text: 'Largest First' },
      { value: 'size-asc', text: 'Smallest First' }
    ];

    sortOptions.forEach(option => {
      const optionElement = document.createElement('option');
      optionElement.value = option.value;
      optionElement.textContent = option.text;
      sortSelect.appendChild(optionElement);
    });

    // Refresh button
    const refreshBtn = document.createElement('button');
    refreshBtn.id = 'refreshBtn';
    refreshBtn.innerHTML = '↻';
    refreshBtn.title = 'Refresh';
    refreshBtn.style.cssText = `
      background: var(--primary-color);
      color: var(--text-inverse);
      border: none;
      border-radius: var(--border-radius-md);
      padding: var(--spacing-md);
      font-size: 1.2rem;
      cursor: pointer;
      min-height: 44px;
      min-width: 44px;
      transition: var(--transition-fast);
    `;

    searchBox.appendChild(searchInput);
    searchBox.appendChild(clearSearchBtn);
    searchBox.appendChild(sortSelect);
    searchBox.appendChild(refreshBtn);
    searchContainer.appendChild(searchBox);

    return searchContainer;
  }

  private createPDFContainer(): HTMLElement {
    const pdfContainer = document.createElement('div');
    pdfContainer.className = 'pdf-container';

    // Loading state
    const loadingState = document.createElement('div');
    loadingState.id = 'loadingState';
    loadingState.style.cssText = `
      text-align: center;
      padding: var(--spacing-2xl);
    `;

    const spinner = document.createElement('div');
    spinner.className = 'spinner';
    spinner.style.cssText = `
      width: 40px;
      height: 40px;
      border: 4px solid var(--border-color);
      border-top: 4px solid var(--primary-color);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto var(--spacing-md);
    `;

    const loadingText = document.createElement('p');
    loadingText.textContent = 'Loading PDFs...';
    loadingText.style.cssText = `
      color: var(--text-secondary);
      font-size: 1.1rem;
    `;

    loadingState.appendChild(spinner);
    loadingState.appendChild(loadingText);

    // PDF Grid
    const pdfGrid = document.createElement('div');
    pdfGrid.id = 'pdfGrid';
    pdfGrid.style.cssText = `
      display: none;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: var(--spacing-lg);
    `;

    // Empty state
    const emptyState = document.createElement('div');
    emptyState.id = 'emptyState';
    emptyState.style.cssText = `
      display: none;
      text-align: center;
      padding: var(--spacing-2xl);
    `;

    const emptyIcon = document.createElement('div');
    emptyIcon.innerHTML = '📄';
    emptyIcon.style.cssText = `
      font-size: 3rem;
      margin-bottom: var(--spacing-md);
    `;

    const emptyTitle = document.createElement('h3');
    emptyTitle.textContent = 'No PDFs Found';
    emptyTitle.style.cssText = `
      color: var(--text-secondary);
      margin-bottom: var(--spacing-sm);
    `;

    const emptyText = document.createElement('p');
    emptyText.textContent = 'No PDFs found in this container';
    emptyText.style.cssText = `
      color: var(--text-muted);
    `;

    emptyState.appendChild(emptyIcon);
    emptyState.appendChild(emptyTitle);
    emptyState.appendChild(emptyText);

    // Pagination
    const pagination = document.createElement('div');
    pagination.id = 'pagination';
    pagination.style.cssText = `
      display: none;
      text-align: center;
      margin-top: var(--spacing-xl);
    `;

    const prevPageBtn = document.createElement('button');
    prevPageBtn.id = 'prevPage';
    prevPageBtn.textContent = 'Previous';
    prevPageBtn.disabled = true;
    prevPageBtn.style.cssText = `
      background: var(--primary-color);
      color: var(--text-inverse);
      border: none;
      padding: var(--spacing-md) var(--spacing-lg);
      border-radius: var(--border-radius-md);
      margin-right: var(--spacing-md);
      cursor: pointer;
      min-height: 44px;
      min-width: 44px;
    `;

    const pageInfo = document.createElement('span');
    pageInfo.id = 'pageInfo';
    pageInfo.textContent = 'Page 1 of 1';
    pageInfo.style.cssText = `
      margin: 0 var(--spacing-md);
      color: var(--text-secondary);
    `;

    const nextPageBtn = document.createElement('button');
    nextPageBtn.id = 'nextPage';
    nextPageBtn.textContent = 'Next';
    nextPageBtn.style.cssText = `
      background: var(--primary-color);
      color: var(--text-inverse);
      border: none;
      padding: var(--spacing-md) var(--spacing-lg);
      border-radius: var(--border-radius-md);
      margin-left: var(--spacing-md);
      cursor: pointer;
      min-height: 44px;
      min-width: 44px;
    `;

    pagination.appendChild(prevPageBtn);
    pagination.appendChild(pageInfo);
    pagination.appendChild(nextPageBtn);

    pdfContainer.appendChild(loadingState);
    pdfContainer.appendChild(pdfGrid);
    pdfContainer.appendChild(emptyState);
    pdfContainer.appendChild(pagination);

    return pdfContainer;
  }

  public mount(target: HTMLElement): void {
    this.isDestroyed = false;

    // For container pages, we need to replace the entire app content 
    // because they have their own header with back navigation
    target.innerHTML = '';
    target.appendChild(this.container);
    
    // Initialize Container Browser after DOM is mounted
    this.mountTimeoutId = window.setTimeout(() => {
      if (this.isDestroyed) {
        return;
      }
      this.initializeContainerBrowser();
    }, 0);
  }

  private initializeContainerBrowser(): void {
    if (this.isDestroyed || !document.body.contains(this.container)) {
      return;
    }

    try {
      // Prevent multiple instances for the same container
      if (this.containerBrowser) {
        console.log('ContainerBrowser already initialized, skipping...');
        return;
      }

      // Create a new ContainerBrowser instance for this container
      this.containerBrowser = new ContainerBrowser({
        containerId: this.config.containerId,
        containerName: this.config.containerName,
        containerColor: this.config.containerColor,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
        signalRHubUrl: import.meta.env.VITE_SIGNALR_HUB_URL || '/api'
      });
      
      // Delay notification to avoid interfering with SignalR connection state display
      if (this.containerBrowser) {
        this.loadedNotificationTimeoutId = window.setTimeout(() => {
          if (!this.isDestroyed && this.containerBrowser) {
            this.containerBrowser.showNotification(`${this.config.containerName} loaded successfully`, 'success', 2000);
          }
        }, 3000); // Wait 3 seconds to ensure SignalR connection is stable
      }
      
    } catch (error) {
      console.error('Failed to initialize Container Browser:', error);
      showStatusNotification('Failed to load container. Please try again.', 'error', 5000);
    }
  }

  public destroy(): void {
    this.isDestroyed = true;

    if (this.mountTimeoutId !== null) {
      window.clearTimeout(this.mountTimeoutId);
      this.mountTimeoutId = null;
    }

    if (this.loadedNotificationTimeoutId !== null) {
      window.clearTimeout(this.loadedNotificationTimeoutId);
      this.loadedNotificationTimeoutId = null;
    }

    if (this.containerBrowser) {
      this.containerBrowser.destroy();
      this.containerBrowser = null;
    }
    this.container.remove();
  }
}
